import { useEffect, useRef, useState } from "react";
import type { z } from "zod";
import type { exportCounterpartyTinBodySchema } from "@common/schemas/counterpartyTin.schema";
import { mintTinExportGrant } from "@api/counterpartyTin.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useTypedFlags } from "@hooks/useTypedFlags";
import {
    getVgsConfig,
    getVgsProxyOrigin,
    VGS_TIN_EXPORT_PATH,
} from "@lib/vgs/vgsConfig";
import { postVgsDownloadForm } from "@lib/vgs/postVgsDownloadForm";
import { useScope } from "@state/scopeContext.lib";
import { useCanManageCounterpartyTin } from "@components/counterparty/tin/useCanManageCounterpartyTin";

const EXPORT_FAILED_MESSAGE = "Failed to export tax IDs";
const DOWNLOAD_COOLDOWN_MS = 10_000;

type TinExportStatus = "idle" | "minting" | "stepUpRequired" | "downloading";

interface TinExportParams {
    taxYear: number;
    entityId: number | undefined;
}

/**
 * The CSV leaves our backend with vault aliases in the Federal ID column and
 * the VGS route reveals them on the way out, so only the browser's download
 * ever holds the tax IDs.
 */
export function useForm1099NecTinExport({
    taxYear,
    entityId,
}: TinExportParams) {
    const { workspaceId } = useScope();
    const { counterpartyTinExport } = useTypedFlags();
    const canManageTin = useCanManageCounterpartyTin();
    const toaster = useToaster();
    const [status, setStatus] = useState<TinExportStatus>("idle");
    const cooldownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
        null,
    );
    const config = getVgsConfig();

    const clearCooldown = () => {
        if (cooldownTimeoutRef.current !== null) {
            clearTimeout(cooldownTimeoutRef.current);
            cooldownTimeoutRef.current = null;
        }
    };

    useEffect(() => clearCooldown, []);

    const exportWithTins = async () => {
        if (!config || !workspaceId || entityId === undefined) {
            return;
        }
        clearCooldown();
        setStatus("minting");
        const result = await mintTinExportGrant({
            workspaceId,
            taxYear,
            entityId,
        });
        if (result.status === "stepUpRequired") {
            setStatus("stepUpRequired");
            return;
        }
        if (result.status === "failed") {
            setStatus("idle");
            toaster.error(result.message ?? EXPORT_FAILED_MESSAGE);
            return;
        }
        setStatus("downloading");
        cooldownTimeoutRef.current = setTimeout(() => {
            cooldownTimeoutRef.current = null;
            setStatus("idle");
        }, DOWNLOAD_COOLDOWN_MS);
        postVgsDownloadForm({
            url: `${getVgsProxyOrigin(config)}${VGS_TIN_EXPORT_PATH}`,
            fields: {
                grant: result.grant,
            } satisfies z.infer<typeof exportCounterpartyTinBodySchema>,
            onFailure: () => {
                clearCooldown();
                setStatus("idle");
                toaster.error(EXPORT_FAILED_MESSAGE);
            },
        });
    };

    return {
        isAvailable: counterpartyTinExport && canManageTin && config !== null,
        isMinting: status === "minting",
        isDownloading: status === "downloading",
        isStepUpRequired: status === "stepUpRequired",
        exportWithTins,
        dismissStepUp: () => setStatus("idle"),
    };
}
